import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Activity,
  Thermometer,
  Droplets,
  Wind,
  UtensilsCrossed,
  MapPin,
  Wifi,
  WifiOff,
  Battery,
  AlertTriangle,
  Phone,
  Calendar,
  FileText,
  Clock,
  User,
  Home,
  Settings,
  FlaskConical,
  CalendarPlus,
  X,
  Mail,
  Users,
  Stethoscope,
  Check
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  getResident,
  getDevices,
  getOpenAlerts,
  getRecentEvents,
  getResidentHistory
} from '../lib/supabase';
import { useTranslation } from '../contexts/TranslationContext';

function StatCard({ icon: Icon, label, value, subValue, status }) {
  const statusColors = {
    ok: 'border-green-500/30 bg-green-500/5',
    warning: 'border-orange-500/30 bg-orange-500/5',
    urgent: 'border-red-500/30 bg-red-500/5',
    neutral: 'border-[#27272a]'
  };
  
  return (
    <div className={cn(
      "bg-[#18181b] border rounded-lg p-4",
      statusColors[status || 'neutral']
    )}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className={cn(
          "h-5 w-5",
          status === 'ok' && "text-green-500",
          status === 'warning' && "text-orange-500",
          status === 'urgent' && "text-red-500",
          !status && "text-[#52525b]"
        )} />
        <span className="text-sm text-[#a1a1aa]">{label}</span>
      </div>
      <div className="font-barlow font-bold text-2xl">{value}</div>
      {subValue && <div className="text-xs text-[#52525b] mt-1 font-mono">{subValue}</div>}
    </div>
  );
}

function HistoryItem({ type, data, timestamp }) {
  const isAlert = type === 'alert';
  
  const eventLabels = {
    activity_score: 'Activity Score',
    fridge_open: 'Fridge Opened',
    iaqm_reading: 'IAQM Reading',
    tracker_outside: 'Tracker Update',
    fall_detected: 'Fall Detected',
    medical_need: 'Medical Need'
  };
  
  const alertLabels = {
    fall: 'Fall Alert',
    medical: 'Medical Alert',
    device_offline: 'Device Offline',
    iaqm: 'Air Quality Alert'
  };
  
  return (
    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-[#27272a]/50 transition-colors">
      <div className={cn(
        "w-2 h-2 rounded-full mt-2",
        isAlert ? "bg-red-500" : "bg-blue-500"
      )} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">
            {isAlert ? alertLabels[data.alert_type] || data.alert_type : eventLabels[data.event_type] || data.event_type}
          </span>
          {isAlert && (
            <Badge variant="outline" className={cn(
              "text-[10px]",
              data.severity === 'critical' && "text-red-500 border-red-500/30",
              data.severity === 'high' && "text-orange-500 border-orange-500/30"
            )}>
              {data.severity}
            </Badge>
          )}
          {isAlert && (
            <Badge variant="outline" className="text-[10px]">
              {data.status}
            </Badge>
          )}
        </div>
        {!isAlert && data.data && (
          <div className="text-xs text-[#a1a1aa] mt-1 font-mono">
            {JSON.stringify(data.data)}
          </div>
        )}
        <div className="text-xs text-[#52525b] mt-1 font-mono">
          {new Date(timestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function ContactCard({ name, relation, phone, email }) {
  return (
    <div className="bg-[#09090b]/50 border border-[#27272a] rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
          <User className="h-5 w-5 text-blue-500" />
        </div>
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-xs text-[#52525b]">{relation}</div>
        </div>
      </div>
      <div className="space-y-1 mt-3">
        <div className="flex items-center gap-2 text-sm text-[#a1a1aa]">
          <Phone className="h-3 w-3" />
          <span className="font-mono">{phone}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#a1a1aa]">
          <Mail className="h-3 w-3" />
          <span>{email}</span>
        </div>
      </div>
    </div>
  );
}

export default function ResidentProfile() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [resident, setResident] = useState(null);
  const [device, setDevice] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState({ events: [], alerts: [] });
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMapDialog, setShowMapDialog] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        
        const [residentData, devicesData, alertsData, eventsData, historyData] = await Promise.all([
          getResident(id),
          getDevices(),
          getOpenAlerts(),
          getRecentEvents(),
          getResidentHistory(id)
        ]);
        
        // Fetch appointments for this resident
        try {
          const appointmentsRes = await fetch(`${BACKEND_URL}/api/appointments?resident_id=${id}`);
          if (appointmentsRes.ok) {
            setAppointments(await appointmentsRes.json());
          }
        } catch (e) {
          console.error('Failed to fetch appointments:', e);
        }
        
        setResident(residentData);
        setDevice(devicesData?.find(d => d.resident_id === id));
        setAlerts(alertsData?.filter(a => a.resident_id === id) || []);
        setEvents(eventsData?.filter(e => e.resident_id === id) || []);
        setHistory(historyData);
      } catch (err) {
        console.error('Failed to fetch resident:', err);
        toast.error('Failed to load resident data');
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, [id, BACKEND_URL]);

  // Get latest metrics
  const latestActivity = events.find(e => e.event_type === 'activity_score');
  const latestIAQM = events.find(e => e.event_type === 'iaqm_reading');
  const latestOutside = events.find(e => e.event_type === 'tracker_outside');
  
  // Check breakfast
  const today = new Date().toDateString();
  const breakfastEvent = events.find(e => 
    e.event_type === 'fridge_open' && 
    new Date(e.ts).toDateString() === today
  );
  
  // Determine statuses
  const hasFallAlert = alerts.some(a => a.alert_type === 'fall');
  const hasMedicalAlert = alerts.some(a => a.alert_type === 'medical');
  const highCO2 = latestIAQM?.data?.co2_ppm > 1200;
  const isOffline = device?.status === 'offline';
  const isOutside = latestOutside?.data?.outside;

  // Mock contacts for demo
  const contacts = [
    { name: 'Marie Ozola', relation: 'Daughter', phone: '+371 2911 2233', email: 'marie.ozola@email.lv' },
    { name: 'Jānis Ozols', relation: 'Son', phone: '+371 2922 3344', email: 'janis.ozols@email.lv' },
  ];

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-[#27272a] rounded w-1/3 mb-4" />
          <div className="h-4 bg-[#27272a] rounded w-1/4 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-[#27272a] rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!resident) {
    return (
      <div className="p-6 md:p-8">
        <Link to="/" className="flex items-center gap-2 text-[#a1a1aa] hover:text-[#fafafa] mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-8 text-center">
          <User className="h-12 w-12 text-[#52525b] mx-auto mb-4" />
          <h3 className="font-barlow font-bold text-lg mb-2">Resident Not Found</h3>
          <p className="text-[#a1a1aa] text-sm">
            No resident found with ID: {id}
          </p>
        </div>
      </div>
    );
  }

  // Combine history for timeline
  const timeline = [
    ...history.events.map(e => ({ type: 'event', data: e, timestamp: e.ts })),
    ...history.alerts.map(a => ({ type: 'alert', data: a, timestamp: a.created_at }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 50);

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-16 sm:pt-4" data-testid="resident-profile-page">
      {/* Header */}
      <Link to="/" className="flex items-center gap-2 text-[#a1a1aa] hover:text-[#fafafa] mb-6">
        <ArrowLeft className="h-4 w-4" />
        {t('back_to_dashboard')}
      </Link>
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-barlow font-bold text-xl sm:text-2xl md:text-3xl tracking-tight">
            {resident.first_name} {resident.last_name}
          </h1>
          <p className="text-[#a1a1aa] text-sm mt-1 font-mono">
            ID: {resident.id}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Book Medical Appointment Button */}
          <Link to={`/appointments?resident_id=${id}`}>
            <Button variant="default" data-testid="book-medical-btn">
              <CalendarPlus className="h-4 w-4 mr-2" />
              {t('book_appointment')}
            </Button>
          </Link>
          
          <Link to={`/settings/${id}`}>
            <Button variant="outline" data-testid="go-to-settings">
              <Settings className="h-4 w-4 mr-2" />
              {t('settings')}
            </Button>
          </Link>
          <Link to={`/simulation?resident_id=${id}`}>
            <Button variant="outline" data-testid="go-to-simulation">
              <FlaskConical className="h-4 w-4 mr-2" />
              Simulation
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-[#18181b] border border-[#27272a]">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="apartment" data-testid="tab-apartment">Apartment</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Alert Banners */}
          {(hasFallAlert || hasMedicalAlert) && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-500">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-bold">Active Alerts</span>
              </div>
              <div className="mt-2 space-y-1">
                {alerts.map(alert => (
                  <div key={alert.id} className="text-sm">
                    <Badge variant="outline" className="text-red-500 border-red-500/30 mr-2">
                      {alert.alert_type.toUpperCase()}
                    </Badge>
                    <span className="text-[#a1a1aa] font-mono text-xs">
                      {new Date(alert.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={hasFallAlert ? AlertTriangle : Activity}
              label="Fall Detection"
              value={hasFallAlert ? 'ALERT' : 'OK'}
              status={hasFallAlert ? 'urgent' : 'ok'}
            />
            <StatCard
              icon={hasMedicalAlert ? AlertTriangle : Phone}
              label="Medical Alert"
              value={hasMedicalAlert ? 'ACTIVE' : 'None'}
              status={hasMedicalAlert ? 'urgent' : 'ok'}
            />
            <StatCard
              icon={UtensilsCrossed}
              label="Breakfast"
              value={breakfastEvent ? 'OK' : 'Missed'}
              subValue={breakfastEvent ? new Date(breakfastEvent.ts).toLocaleTimeString() : null}
              status={breakfastEvent ? 'ok' : 'warning'}
            />
            <StatCard
              icon={isOffline ? WifiOff : Wifi}
              label="Bracelet"
              value={isOffline ? 'Offline' : 'Online'}
              subValue={device?.battery_percent ? `${device.battery_percent}% battery` : null}
              status={isOffline ? 'urgent' : 'ok'}
            />
          </div>
          
          {/* Outside Status - Clickable */}
          {isOutside && (
            <div 
              className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 cursor-pointer hover:bg-blue-500/20 transition-colors"
              onClick={() => setShowMapDialog(true)}
              data-testid="outside-status-card"
            >
              <div className="flex items-center gap-2 text-blue-500">
                <MapPin className="h-5 w-5" />
                <span className="font-bold">Resident is Outside</span>
                <span className="text-xs ml-auto text-[#a1a1aa]">Click to view location</span>
              </div>
              <div className="text-sm text-[#a1a1aa] mt-1">
                Last update: {latestOutside ? new Date(latestOutside.ts).toLocaleString() : 'Unknown'}
              </div>
            </div>
          )}
          
          {/* IAQM */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <h3 className="font-barlow font-bold text-lg mb-4">Indoor Air Quality</h3>
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                icon={Thermometer}
                label="Temperature"
                value={latestIAQM?.data?.temp_c?.toFixed(1) || '--'}
                subValue="°C"
                status="neutral"
              />
              <StatCard
                icon={Droplets}
                label="Humidity"
                value={latestIAQM?.data?.hum_pct || '--'}
                subValue="%"
                status="neutral"
              />
              <StatCard
                icon={Wind}
                label="CO2"
                value={latestIAQM?.data?.co2_ppm || '--'}
                subValue="ppm"
                status={highCO2 ? 'warning' : 'neutral'}
              />
            </div>
          </div>
          
          {/* Activity & Outside */}
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              icon={Activity}
              label="Activity Score"
              value={latestActivity?.data?.score || '--'}
              subValue={latestActivity ? `Updated ${new Date(latestActivity.ts).toLocaleTimeString()}` : null}
              status="neutral"
            />
            <div 
              className={cn(
                "bg-[#18181b] border rounded-lg p-4 cursor-pointer transition-colors",
                isOutside ? "border-blue-500/30 hover:bg-blue-500/5" : "border-[#27272a]"
              )}
              onClick={() => isOutside && setShowMapDialog(true)}
            >
              <div className="flex items-center gap-3 mb-2">
                <MapPin className={cn("h-5 w-5", isOutside ? "text-blue-500" : "text-[#52525b]")} />
                <span className="text-sm text-[#a1a1aa]">Location</span>
              </div>
              <div className="font-barlow font-bold text-2xl">{isOutside ? 'Outside' : 'Inside'}</div>
              {latestOutside && (
                <div className="text-xs text-[#52525b] mt-1 font-mono">
                  Updated {new Date(latestOutside.ts).toLocaleTimeString()}
                </div>
              )}
              {isOutside && <div className="text-xs text-blue-500 mt-1">Click to view map</div>}
            </div>
          </div>
        </TabsContent>
        
        {/* Apartment Tab */}
        <TabsContent value="apartment" className="space-y-6">
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <h3 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
              <Home className="h-5 w-5" />
              Apartment Information
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-[#52525b] uppercase tracking-wider">Doctor</label>
                <p className="mt-1">Dr. Marie Laurent</p>
              </div>
              <div>
                <label className="text-sm text-[#52525b] uppercase tracking-wider">Cleaning Schedule</label>
                <p className="mt-1">Monday, Wednesday, Friday</p>
              </div>
              <div>
                <label className="text-sm text-[#52525b] uppercase tracking-wider">Last Nurse Visit</label>
                <p className="mt-1 font-mono text-sm">2025-01-13 09:30</p>
              </div>
              <div>
                <label className="text-sm text-[#52525b] uppercase tracking-wider">Emergency Contact</label>
                <p className="mt-1">+371 2000 0001</p>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-[#27272a]">
              <label className="text-sm text-[#52525b] uppercase tracking-wider flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Notes
              </label>
              <p className="mt-2 text-[#a1a1aa]">
                Requires assistance with mobility. Prefers breakfast before 9 AM. 
                Regular medication at 8:00 and 20:00.
              </p>
            </div>
          </div>
          
          {/* Close Contacts Section */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <h3 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Close Contacts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contacts.map((contact, i) => (
                <ContactCard key={i} {...contact} />
              ))}
            </div>
          </div>
          
          {/* Appointments Section */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-barlow font-bold text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Upcoming Appointments
              </h3>
              <Link to={`/appointments?resident_id=${id}`}>
                <Button variant="outline" size="sm">
                  <CalendarPlus className="h-4 w-4 mr-1" />
                  Book New
                </Button>
              </Link>
            </div>
            {appointments.filter(a => a.status !== 'completed' && a.status !== 'cancelled').length > 0 ? (
              <div className="space-y-3">
                {appointments
                  .filter(a => a.status !== 'completed' && a.status !== 'cancelled')
                  .slice(0, 3)
                  .map(apt => (
                    <div key={apt.id} className="flex items-center justify-between p-3 bg-[#09090b] rounded-lg border border-[#27272a]">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/10">
                          <Stethoscope className="h-4 w-4 text-blue-500" />
                        </div>
                        <div>
                          <div className="font-medium text-sm capitalize">{apt.type.replace('_', ' ')}</div>
                          <div className="text-xs text-[#52525b]">
                            {apt.doctor_name ? `Dr. ${apt.doctor_name}` : 'Doctor TBD'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{new Date(apt.date).toLocaleDateString()}</div>
                        <div className="text-xs text-[#52525b]">{apt.time}</div>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[10px] ml-2",
                          apt.status === 'scheduled' && "text-blue-500 border-blue-500/30",
                          apt.status === 'confirmed' && "text-green-500 border-green-500/30"
                        )}
                      >
                        {apt.status}
                      </Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-6 text-[#52525b]">
                <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming appointments</p>
                <Link to={`/appointments?resident_id=${id}`} className="text-blue-500 text-sm hover:underline">
                  Book an appointment
                </Link>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-barlow font-bold text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Event History
              </h3>
              <span className="text-sm text-[#52525b]">Last 50 events</span>
            </div>
            
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {timeline.length > 0 ? (
                timeline.map((item, i) => (
                  <HistoryItem
                    key={`${item.type}-${item.data.id || i}`}
                    type={item.type}
                    data={item.data}
                    timestamp={item.timestamp}
                  />
                ))
              ) : (
                <div className="text-center text-[#52525b] py-8">
                  No history available
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Map Dialog */}
      <Dialog open={showMapDialog} onOpenChange={setShowMapDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              Resident Location
            </DialogTitle>
          </DialogHeader>
          <div className="relative">
            {/* Fake Map */}
            <div className="w-full h-[400px] bg-[#09090b] rounded-lg overflow-hidden relative">
              {/* Map background pattern */}
              <div className="absolute inset-0 opacity-20">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3f3f46" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>
              
              {/* Fake streets */}
              <div className="absolute inset-0">
                <div className="absolute top-1/3 left-0 right-0 h-8 bg-[#27272a]/50" />
                <div className="absolute top-2/3 left-0 right-0 h-6 bg-[#27272a]/30" />
                <div className="absolute left-1/4 top-0 bottom-0 w-6 bg-[#27272a]/40" />
                <div className="absolute left-2/3 top-0 bottom-0 w-8 bg-[#27272a]/50" />
              </div>
              
              {/* Buildings */}
              <div className="absolute top-[15%] left-[10%] w-16 h-12 bg-[#3f3f46]/50 rounded" />
              <div className="absolute top-[20%] left-[35%] w-20 h-16 bg-[#3f3f46]/50 rounded" />
              <div className="absolute top-[50%] left-[15%] w-24 h-20 bg-[#3f3f46]/50 rounded" />
              <div className="absolute top-[45%] right-[20%] w-32 h-24 bg-[#3f3f46]/50 rounded" />
              <div className="absolute bottom-[20%] left-[40%] w-20 h-14 bg-[#3f3f46]/50 rounded" />
              
              {/* Residence building */}
              <div className="absolute top-[40%] left-[45%] w-12 h-12 bg-blue-500/30 rounded border-2 border-blue-500" />
              
              {/* Pin marker */}
              <div className="absolute top-[35%] left-[60%] transform -translate-x-1/2">
                <div className="relative">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/50 animate-bounce">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45" />
                </div>
              </div>
              
              {/* Labels */}
              <div className="absolute bottom-4 left-4 text-xs text-[#52525b]">
                Eldera Residence Area
              </div>
              <div className="absolute top-4 right-4 bg-[#18181b]/80 px-2 py-1 rounded text-xs">
                <span className="text-red-500">●</span> {resident?.first_name} {resident?.last_name}
              </div>
            </div>
            
            {/* Location info */}
            <div className="mt-4 p-3 bg-[#09090b] rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Current Location</div>
                  <div className="text-xs text-[#52525b] mt-1">Near: Park Area - 150m from residence</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#52525b]">Last update</div>
                  <div className="text-sm font-mono">{latestOutside ? new Date(latestOutside.ts).toLocaleTimeString() : '--:--'}</div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
